import React, { useState, useEffect } from 'react';
import { Calculator, RefreshCw, Shield, Home, Wallet, Info } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { ASSUMPTIONS } from '../utils/calculationEngine';

export const QuickStartForm = ({ onCalculate, loading, initialData, showAsUpdate }) => {
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    current_age: 30,
    retirement_age: 67,
    current_net_income_monthly: 3000,
    salary_growth_rate: ASSUMPTIONS.SALARY_GROWTH_RATE,
    desired_retirement_income_monthly: null,
    existing_pension_monthly: null,
    existing_private_retirement_monthly: 0,
    monthly_pension_contribution: 250,
    current_investable_assets: 30000,
    monthly_savings: 250,
    inflation_rate: ASSUMPTIONS.INFLATION_RATE,
    expected_return: ASSUMPTIONS.GROSS_MARKET_RETURN,
    has_income_protection: false,
    has_real_estate: false,
    has_notgroschen: false,
    notgroschen_amount: 0,
  });

  const [retirementPercentage, setRetirementPercentage] = useState(80);
  const [isFromHomepage, setIsFromHomepage] = useState(false);

  // Load values from homepage navigation (URL params or sessionStorage)
  useEffect(() => {
    const fromHomepage = searchParams.get('from_homepage') === 'true';
    
    if (fromHomepage) {
      setIsFromHomepage(true);
      
      // Try to get values from sessionStorage first (more reliable)
      const storedInputs = sessionStorage.getItem('homepage_analysis_inputs');
      
      if (storedInputs) {
        try {
          const parsed = JSON.parse(storedInputs);
          setFormData(prev => ({
            ...prev,
            monthly_savings: parsed.monthly_savings || prev.monthly_savings,
            current_age: parsed.current_age || prev.current_age,
            retirement_age: parsed.retirement_age || prev.retirement_age,
            current_net_income_monthly: parsed.current_net_income_monthly || prev.current_net_income_monthly,
            current_investable_assets: parsed.current_investable_assets || 0,
          }));
          // Clear the stored data after use
          sessionStorage.removeItem('homepage_analysis_inputs');
        } catch (e) {
          console.error('Error parsing homepage inputs:', e);
        }
      } else {
        // Fallback to URL params
        const monthly_savings = parseInt(searchParams.get('monthly_savings'));
        const current_age = parseInt(searchParams.get('current_age'));
        const retirement_age = parseInt(searchParams.get('retirement_age'));
        const income = parseInt(searchParams.get('current_net_income_monthly'));
        
        setFormData(prev => ({
          ...prev,
          monthly_savings: !isNaN(monthly_savings) ? monthly_savings : prev.monthly_savings,
          current_age: !isNaN(current_age) ? current_age : prev.current_age,
          retirement_age: !isNaN(retirement_age) ? retirement_age : prev.retirement_age,
          current_net_income_monthly: !isNaN(income) ? income : prev.current_net_income_monthly,
          current_investable_assets: 0, // Homepage demo assumes 0 starting capital
        }));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.desired_retirement_income_monthly && initialData.current_net_income_monthly) {
        const pct = Math.round((initialData.desired_retirement_income_monthly / initialData.current_net_income_monthly) * 100);
        setRetirementPercentage(Math.min(100, Math.max(60, pct)));
      }
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const desiredRetirement = Math.round(formData.current_net_income_monthly * (retirementPercentage / 100));
    
    const submissionData = {
      ...formData,
      desired_retirement_income_monthly: desiredRetirement,
      emergency_fund_months: formData.has_notgroschen ? 3 : 0
    };
    
    onCalculate(submissionData);
  };

  const yearsToRetirement = formData.retirement_age - formData.current_age;
  const incomeAtRetirement = Math.round(
    formData.current_net_income_monthly * Math.pow(1 + formData.salary_growth_rate, yearsToRetirement)
  );
  const estimatedPension = Math.round(formData.current_net_income_monthly * ASSUMPTIONS.STATUTORY_PENSION_REPLACEMENT_RATE);
  const desiredRetirementIncome = Math.round(formData.current_net_income_monthly * (retirementPercentage / 100));

  // Selectable card component for checkboxes
  const SelectableCard = ({ id, checked, onChange, icon: Icon, label, sublabel }) => (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex-1 min-w-0 p-2.5 sm:p-3 rounded-lg border-2 transition-all duration-200 text-left ${
        checked 
          ? 'border-cobalt bg-cobalt/5' 
          : 'border-black/10 bg-white hover:border-black/20'
      }`}
      data-testid={`card-${id}`}
    >
      <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
          checked ? 'bg-cobalt/10' : 'bg-black/5'
        }`}>
          <Icon className={`w-4 h-4 ${checked ? 'text-cobalt' : 'text-slate'}`} />
        </div>
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className={`text-xs font-medium ${checked ? 'text-navy' : 'text-charcoal'}`}>
            {label}
          </div>
          <div className="text-[10px] text-slate hidden sm:block">{sublabel}</div>
        </div>
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 hidden sm:flex ${
          checked ? 'border-cobalt bg-cobalt' : 'border-black/20'
        }`}>
          {checked && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
        </div>
      </div>
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg border border-black/5 p-4 sm:p-6">
        
        {/* Homepage Prefill Notice */}
        {isFromHomepage && !showAsUpdate && (
          <div className="mb-4 sm:mb-5 p-2.5 sm:p-3 bg-cobalt/5 border border-cobalt/20 rounded-lg flex items-center gap-2">
            <Info className="w-4 h-4 text-cobalt flex-shrink-0" />
            <span className="text-xs sm:text-sm text-cobalt">
              Ihre Werte aus der Homepage-Demo wurden übernommen.
            </span>
          </div>
        )}
        
        {/* Compact Header */}
        {!showAsUpdate && (
          <div className="flex items-center justify-between mb-4 sm:mb-5 pb-3 sm:pb-4 border-b border-black/5">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-cobalt/10 rounded-xl flex items-center justify-center">
                <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-cobalt" />
              </div>
              <div>
                <h2 className="font-outfit text-base sm:text-lg font-bold text-navy">Finanzanalyse</h2>
                <p className="text-[10px] sm:text-xs text-slate">Alle Daten bleiben vertraulich</p>
              </div>
            </div>
          </div>
        )}

        {/* SECTION: Persönliche Daten */}
        <div className="mb-4 sm:mb-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate font-medium mb-2 sm:mb-3">Persönliche Daten</div>
          
          {/* Row 1: Age inputs */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div>
              <Label className="text-[10px] sm:text-xs text-charcoal mb-1 sm:mb-1.5 block">Aktuelles Alter</Label>
              <Input
                type="number"
                value={formData.current_age}
                onChange={(e) => handleChange('current_age', parseInt(e.target.value))}
                className="h-9 sm:h-10 bg-white border-black/10 text-sm"
                data-testid="input-current-age"
              />
            </div>
            <div>
              <Label className="text-[10px] sm:text-xs text-charcoal mb-1 sm:mb-1.5 block">Renteneintritt</Label>
              <Input
                type="number"
                value={formData.retirement_age}
                onChange={(e) => handleChange('retirement_age', parseInt(e.target.value))}
                className="h-9 sm:h-10 bg-white border-black/10 text-sm"
                data-testid="input-retirement-age"
              />
            </div>
          </div>

          {/* Row 2: Income and Savings with sliders */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="p-2.5 sm:p-3 bg-bone/50 rounded-lg">
              <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                <Label className="text-[10px] sm:text-xs text-charcoal">Nettoeinkommen</Label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={formData.current_net_income_monthly}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1000;
                      handleChange('current_net_income_monthly', Math.min(Math.max(val, 1000), 15000));
                    }}
                    className="font-mono text-xs sm:text-sm font-bold text-navy text-right bg-transparent border-b border-navy/20 focus:border-navy focus:outline-none w-16 sm:w-20"
                    min={1000}
                    max={15000}
                    data-testid="input-income-text"
                  />
                  <span className="text-[10px] sm:text-xs text-slate">€/M</span>
                </div>
              </div>
              <Slider
                value={[formData.current_net_income_monthly]}
                onValueChange={(val) => handleChange('current_net_income_monthly', val[0])}
                min={1000}
                max={15000}
                step={100}
                className="my-1"
                data-testid="slider-income"
              />
              <div className="flex justify-between text-[9px] sm:text-[10px] text-slate">
                <span>1.000 €</span>
                <span>15.000 €</span>
              </div>
            </div>

            <div className="p-2.5 sm:p-3 bg-bone/50 rounded-lg">
              <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                <Label className="text-[10px] sm:text-xs text-charcoal">Monatl. Sparrate</Label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={formData.monthly_savings}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      handleChange('monthly_savings', Math.min(Math.max(val, 0), 5000));
                    }}
                    className="font-mono text-xs sm:text-sm font-bold text-navy text-right bg-transparent border-b border-navy/20 focus:border-navy focus:outline-none w-16 sm:w-20"
                    min={0}
                    max={5000}
                    data-testid="input-savings-text"
                  />
                  <span className="text-[10px] sm:text-xs text-slate">€/M</span>
                </div>
              </div>
              <Slider
                value={[formData.monthly_savings]}
                onValueChange={(val) => handleChange('monthly_savings', val[0])}
                min={0}
                max={5000}
                step={50}
                className="my-1"
                data-testid="slider-savings"
              />
              <div className="flex justify-between text-[9px] sm:text-[10px] text-slate">
                <span>0 €</span>
                <span>5.000 €</span>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Vermögen */}
        <div className="mb-4 sm:mb-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate font-medium mb-2 sm:mb-3">Vermögen</div>
          
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label className="text-[10px] sm:text-xs text-charcoal mb-1 sm:mb-1.5 block">Investierbar</Label>
              <div className="relative">
                <span className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-slate text-xs sm:text-sm">€</span>
                <Input
                  type="number"
                  value={formData.current_investable_assets}
                  onChange={(e) => handleChange('current_investable_assets', parseInt(e.target.value) || 0)}
                  className="h-9 sm:h-10 bg-white border-black/10 text-sm pl-6 sm:pl-7"
                  data-testid="input-assets"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] sm:text-xs text-charcoal mb-1 sm:mb-1.5 block">Private Rente</Label>
              <div className="relative">
                <span className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-slate text-xs sm:text-sm">€</span>
                <Input
                  type="number"
                  value={formData.existing_private_retirement_monthly}
                  onChange={(e) => handleChange('existing_private_retirement_monthly', parseFloat(e.target.value) || 0)}
                  className="h-9 sm:h-10 bg-white border-black/10 text-sm pl-6 sm:pl-7"
                  placeholder="0"
                  data-testid="input-private-pension"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Rentenziel */}
        <div className="mb-4 sm:mb-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate font-medium mb-2 sm:mb-3">Rentenziel</div>
          
          <div className="p-2.5 sm:p-3 bg-gradient-to-r from-bone to-ice rounded-lg">
            <div className="flex items-center justify-between mb-1.5 sm:mb-2">
              <Label className="text-[10px] sm:text-xs text-charcoal">Gewünschte Rente</Label>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="font-mono text-sm sm:text-lg font-bold text-navy">€{desiredRetirementIncome.toLocaleString()}</span>
                <span className="text-[10px] sm:text-xs text-slate">/M</span>
                <span className="text-[10px] sm:text-xs bg-cobalt/10 text-cobalt px-1.5 sm:px-2 py-0.5 rounded-full font-medium">{retirementPercentage}%</span>
              </div>
            </div>
            <Slider
              value={[retirementPercentage]}
              onValueChange={(val) => setRetirementPercentage(val[0])}
              min={60}
              max={100}
              step={5}
              className="my-1"
              data-testid="slider-retirement-percentage"
            />
            <div className="flex justify-between text-[9px] sm:text-[10px] text-slate">
              <span>60%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Compact info row with tooltip */}
          <div className="flex items-center gap-2 mt-2 sm:mt-3 text-[10px] sm:text-xs">
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 sm:gap-1.5 text-slate cursor-help">
                    <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border border-slate/40 flex items-center justify-center hover:border-cobalt hover:text-cobalt transition-colors">
                      <Info className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                    </div>
                    <span>Gesetzl. Rente: <span className="font-medium text-charcoal">~€{estimatedPension.toLocaleString()}/M</span></span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs p-3 text-xs leading-relaxed">
                  <p>
                    Zur ersten Orientierung wird die gesetzliche Rente pauschal mit 35 % des aktuellen monatlichen Nettoeinkommens angesetzt.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* SECTION: Strategie & Sicherheit */}
        <div className="mb-4 sm:mb-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate font-medium mb-2 sm:mb-3">Strategie & Sicherheit</div>
          
          <div className="flex gap-2 sm:gap-3">
            <SelectableCard
              id="notgroschen"
              checked={formData.has_notgroschen}
              onChange={(val) => handleChange('has_notgroschen', val)}
              icon={Wallet}
              label="Rücklagen"
              sublabel="Liquiditätsreserve"
            />
            <SelectableCard
              id="protection"
              checked={formData.has_income_protection}
              onChange={(val) => handleChange('has_income_protection', val)}
              icon={Shield}
              label="Absicherung"
              sublabel="BU / Grundfähigkeit"
            />
            <SelectableCard
              id="real-estate"
              checked={formData.has_real_estate}
              onChange={(val) => handleChange('has_real_estate', val)}
              icon={Home}
              label="Immobilie"
              sublabel="Eigentum / Anlage"
            />
          </div>
        </div>

        {/* Submit Button - Always visible */}
        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-cobalt to-navy text-white rounded-xl font-medium hover:from-navy hover:to-charcoal transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cobalt/20 text-sm sm:text-base"
          data-testid="calculate-analysis-btn"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Berechnung läuft...</span>
            </div>
          ) : showAsUpdate ? (
            <>
              <RefreshCw className="w-4 h-4" />
              <span>Analyse aktualisieren</span>
            </>
          ) : (
            <>
              <Calculator className="w-4 h-4" />
              <span>Analyse starten</span>
            </>
          )}
        </button>

        {/* Compact footer info */}
        {!showAsUpdate && (
          <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2 sm:gap-4 text-[9px] sm:text-[10px] text-slate flex-wrap">
            <span className="flex items-center gap-1">
              <div className="w-1 h-1 bg-emerald rounded-full" />
              Kostenlos
            </span>
            <span className="flex items-center gap-1">
              <div className="w-1 h-1 bg-emerald rounded-full" />
              Sofortige Ergebnisse
            </span>
            <span className="flex items-center gap-1 hidden sm:flex">
              <div className="w-1 h-1 bg-emerald rounded-full" />
              Transparente Annahmen
            </span>
          </div>
        )}
      </form>
    </div>
  );
};
